'use client';

import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { useBoardDetailQuery } from '@module/queries/customer/customerQueryHooks';
import useModalStore from '@module/stores/useModalStore';
import { useParams, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface Props {
  eventIdx?: number;
}

function EventDetailContainer({ eventIdx: eventIdxProp }: Props) {
  const router = useRouter();
  const params = useParams<{ idx: string }>();
  const { pushModal } = useModalStore();
  const { isLogin, userInfo } = useGetLoginInfoQuery();
  const resolvedEventIdx = eventIdxProp ?? Number(params?.idx ?? '');
  const isValidEventIdx = Number.isFinite(resolvedEventIdx);
  const { data: eventInfo, isError } = useBoardDetailQuery(
    { idx: resolvedEventIdx },
    { retry: false, enabled: isValidEventIdx },
  );

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(300);

  useEffect(() => {
    if (!isValidEventIdx || isError) {
      router.push('/customer/event');
    }
  }, [isError, isValidEventIdx, router]);

  const iframePostMessage = (message: any) => {
    if (!iframeRef.current) return;
    const payload = typeof message !== 'object' ? { type: message } : message;
    iframeRef.current.contentWindow?.postMessage(JSON.stringify(payload), '*');
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      let messageData = null;
      try {
        messageData = JSON.parse(event.data);
        if (typeof messageData !== 'object') return;
      } catch (_err) {
        return;
      }

      switch (messageData.type) {
        case 'resize-height':
          if (!messageData.height || parseInt(messageData.height, 10) < 0) return;
          const height = parseInt(messageData.height, 10);
          setIframeHeight(height);
          break;
        case 'check-login':
          pushModal({ type: 'AUTH_MODAL' });
          break;
        case 'swal':
          if (!messageData.text) return;
          pushModal({ type: 'CONFIRM_MODAL', options: { description: messageData.text } });
          break;
        case 'confirmSwal':
          if (!messageData.text) return;
          pushModal({
            type: 'CONFIRM_CANCEL_MODAL',
            options: {
              description: messageData.text,
              onClick: () => iframePostMessage('confirmOK'),
            },
          });
          break;
        case 'scrollTo':
          window.scrollTo(0, messageData.location || messageData.scrollTo || 0);
          break;
        case 'goCharge':
          pushModal({ type: 'STORE_MODAL' });
          break;
        case 'goPath':
          if (typeof messageData.path !== 'string') return;
          router.push(messageData.path);
          break;
        case 'checkLoginInfo':
          iframePostMessage({
            type: 'checkLoginInfo',
            data: {
              isLogin: isLogin,
              idx: userInfo?.idx || 0,
              id: userInfo?.id || '',
              nick: userInfo?.nick || '',
            },
          });
        default:
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [isLogin, userInfo]);

  if (!eventInfo?.data.iframeUrl && !eventInfo?.data.imgMainSrc) return null; // iframeUrl, imgMainSrc가 함께 존재하는 경우는 없는 것으로 확인 됨.

  return (
    <>
      {eventInfo.data.imgMainSrc && (
        <div className='flex justify-center'>
          <img src={eventInfo.data.imgMainSrc} alt={eventInfo.data.subject} />
        </div>
      )}
      {eventInfo.data.iframeUrl && (
        <iframe
          ref={iframeRef}
          title={`event-detail-${eventInfo.data.idx}`}
          src={eventInfo.data.iframeUrl}
          className='h-full w-full overflow-hidden'
          style={{
            height: `${iframeHeight}px`,
          }}
        />
      )}
    </>
  );
}

export default EventDetailContainer;
